import { useCallback, useRef } from 'preact/hooks';

import WebApp from '@twa-dev/sdk';

import { useIntl } from '@/app/lib/localization/hooks/use-intl';
import { type ErrorData } from '@/app/types/yandex';

import { useGame } from '@/shared/stores/app-store/app-store';

import { CustomErrorCode } from './types';

// ID из документации
// https://fantasyinvest.flowfast.io/space/79846/boards/card/6598586
const SDK_FUNCTION_NAME = 'show_10087681' as const;
const SDK_SCRIPT_URL = '//munqu.com/sdk.js';
const SDK_ZONE_ID = '10087681';

interface MoneyTagAdsProps {
  onReward?: () => void;
  onError?: (error: ErrorData | Error) => void;
  onSkip?: () => void;
}

const makeErrorData = (message: string, code = 'CUSTOM_ERROR'): ErrorData => {
  return {
    type: 'error',
    code,
    text: message,
  };
};

// Функция для загрузки скрипта
const loadMoneyTagScript = (): Promise<void> => {
  return new Promise((resolve, reject) => {
    if (window[SDK_FUNCTION_NAME as keyof Window]) {
      resolve();
      return;
    }

    const script = document.createElement('script');
    script.src = SDK_SCRIPT_URL;
    script.dataset.zone = SDK_ZONE_ID;
    script.dataset.sdk = SDK_FUNCTION_NAME;
    script.async = true;

    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Failed to load Ushoaglosee script'));

    document.head.appendChild(script);
  });
};

export const useMoneyTagAds = ({ onReward, onError }: MoneyTagAdsProps = {}) => {
  const t = useIntl();
  const game = useGame();
  const scriptLoadedRef = useRef(false);
  const backButtonWasVisibleRef = useRef(WebApp?.BackButton?.isVisible || false);

  const handleError = useCallback(
    (err: ErrorData | Error) => {
      if ('text' in err) {
        console.error(err.text, err.code);
      } else {
        console.error(err.message);
      }
      onError?.(err);
    },
    [onError]
  );

  const handleStart = useCallback(() => {
    if (!WebApp?.BackButton) return;
    backButtonWasVisibleRef.current = WebApp.BackButton.isVisible;
    if (backButtonWasVisibleRef.current) WebApp.BackButton.hide();
  }, []);

  const handleEnd = useCallback(() => {
    if (!WebApp?.BackButton) return;
    if (backButtonWasVisibleRef.current) {
      WebApp.BackButton.show();
      backButtonWasVisibleRef.current = true;
    }
  }, []);

  const show = useCallback(() => {
    const showAd = async () => {
      try {
        if (!scriptLoadedRef.current) {
          await loadMoneyTagScript();
          scriptLoadedRef.current = true;
        }

        if (!window[SDK_FUNCTION_NAME as keyof Window]) {
          return handleError(makeErrorData('Failed to load ad script'));
        }

        handleStart();

        try {
          const adParams: Record<string, string> = {};

          if (game?.key) {
            adParams.requestVar = game.key;
          }

          await (
            window[SDK_FUNCTION_NAME as keyof Window] as (
              params?: Record<string, string>
            ) => Promise<void>
          )(Object.keys(adParams).length ? adParams : undefined);
          onReward?.();
        } catch {
          handleError(
            makeErrorData(
              t('No available ad at the moment'),
              CustomErrorCode.NO_AVAILABLE_ADS
            )
          );
        }
      } catch {
        handleError(
          makeErrorData(
            t('An error occurred while showing the ad'),
            CustomErrorCode.GENERAL_ERROR
          )
        );
      } finally {
        handleEnd();
      }
    };

    showAd();
  }, [game?.key, handleError, onReward, t, handleStart, handleEnd]);

  const showBanner = useCallback(
    (bannerConfig?: {
      frequency?: number;
      capping?: number;
      interval?: number;
      timeout?: number;
      everyPage?: boolean;
    }) => {
      const showAdBanner = async () => {
        try {
          if (!scriptLoadedRef.current) {
            await loadMoneyTagScript();
            scriptLoadedRef.current = true;
          }

          if (!window[SDK_FUNCTION_NAME as keyof Window]) {
            return handleError(makeErrorData('Failed to load ad banner script'));
          }

          // Для баннера не скрываем BackButton, так как он не fullscreen

          try {
            const inAppSettings = {
              frequency: 1,
              capping: 0,
              interval: 30,
              timeout: 5,
              everyPage: false,
              ...bannerConfig,
            };

            await (
              window[SDK_FUNCTION_NAME as keyof Window] as (params: {
                type: string;
                inAppSettings: typeof inAppSettings;
              }) => Promise<void>
            )({
              type: 'inApp',
              inAppSettings,
            });

            // Для баннера не вызываем onReward, так как это не rewarded формат
          } catch {
            handleError(
              makeErrorData(
                t('No available banner at the moment'),
                CustomErrorCode.NO_AVAILABLE_ADS
              )
            );
          }
        } catch {
          handleError(
            makeErrorData(
              t('An error occurred while showing the banner'),
              CustomErrorCode.GENERAL_ERROR
            )
          );
        }
      };

      showAdBanner();
    },
    [handleError, t]
  );

  return { show, showBanner };
};
